import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";

import {
  Loader2,
  RefreshCcw,
  AlertCircle,
  Search,
  Store as StoreIcon,
  MapPin,
  Package,
  Plus,
  X,
  Pencil,
  Trash2,
  Mail,
  Globe,
  CheckCircle2,
  Building2,
  Map,
  ArrowUpRight,
  Sparkles,
  Clock3,
  Save,
  LocateFixed,
  Network,
  ExternalLink,
  Upload,
  Image as ImageIcon,
  Camera,
} from "lucide-react";

import API from "../../services/api";

import "../../styles/stores-manager.css";

/* =========================================================
   CONFIG
========================================================= */

const API_BASE =
  process.env.REACT_APP_API_URL || "http://localhost:8000/api";

const MEDIA_BASE = API_BASE.replace(/\/api\/?$/, "");

/* =========================================================
   INITIAL FORM
========================================================= */

const INITIAL_FORM = {
  name: "",
  city: "",
  country: "",
  description: "",
  email: "",
  website: "",
  logo: null,
};

/* =========================================================
   STORE MANAGER
========================================================= */

export default function StoreManager() {
  /* =======================================================
     STATE
  ======================================================= */

  const [stores, setStores] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [search, setSearch] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState({
    ...INITIAL_FORM,
  });

  const [imagePreview, setImagePreview] = useState(null);

  /* =======================================================
     HELPERS
  ======================================================= */

  const clearMessages = useCallback(() => {
    setError("");
    setSuccess("");
  }, []);

  const normalize = (response) => {
    const data = response?.data;

    const result =
      data?.data ||
      data?.results ||
      data ||
      [];

    return Array.isArray(result) ? result : [];
  };

  const getImage = (image) => {
    if (!image) return null;

    if (
      typeof image === "string" &&
      (
        image.startsWith("http://") ||
        image.startsWith("https://") ||
        image.startsWith("blob:") ||
        image.startsWith("data:")
      )
    ) {
      return image;
    }

    return `${MEDIA_BASE}${image.startsWith("/") ? "" : "/"}${image}`;
  };

  const getStoreInitial = (name) => {
    return (
      name?.trim()?.charAt(0)?.toUpperCase() || "D"
    );
  };

  const getStoreProducts = (store) => {
    return Number(store?.total_products || 0);
  };

  const getApiError = (err) => {
    const backendError = err?.response?.data;

    if (typeof backendError === "string") {
      return backendError;
    }

    if (
      backendError?.detail ||
      backendError?.message
    ) {
      return (
        backendError.detail ||
        backendError.message
      );
    }

    if (
      backendError &&
      typeof backendError === "object"
    ) {
      const firstField =
        Object.keys(backendError)[0];

      if (firstField) {
        const value =
          backendError[firstField];

        return Array.isArray(value)
          ? value[0]
          : String(value);
      }
    }

    return "No fue posible guardar la sede.";
  };

  /* =======================================================
     LOAD STORES
  ======================================================= */

  const loadStores = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const response = await API.get("/stores/");

      setStores(normalize(response));
    } catch (err) {
      console.error(err);

      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          "No fue posible cargar las sedes."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  /* =======================================================
     AUTO CLEAR SUCCESS
  ======================================================= */

  useEffect(() => {
    if (!success) return;

    const timer = setTimeout(() => {
      setSuccess("");
    }, 5000);

    return () => clearTimeout(timer);
  }, [success]);

  /* =======================================================
     ESC + BODY LOCK
  ======================================================= */

  useEffect(() => {
    if (!modalOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !saving) {
        setModalOpen(false);
        setEditingId(null);

        setForm({
          ...INITIAL_FORM,
        });

        setImagePreview(null);
      }
    };

    document.addEventListener(
      "keydown",
      handleKeyDown
    );

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener(
        "keydown",
        handleKeyDown
      );

      document.body.style.overflow =
        previousOverflow;
    };
  }, [modalOpen, saving]);

  /* =======================================================
     FORM CHANGE
  ======================================================= */

  const handleChange = (event) => {
    const {
      name,
      value,
    } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));

    if (error) {
      setError("");
    }
  };

  /* =======================================================
     IMAGE UPLOAD
  ======================================================= */

  const handleImageChange = (event) => {
    const file =
      event.target.files?.[0];

    if (!file) return;

    /* Validación */
    if (!file.type.startsWith("image/")) {
      setError(
        "El archivo seleccionado debe ser una imagen."
      );

      event.target.value = "";
      return;
    }

    /* Máximo 5 MB */
    if (file.size > 5 * 1024 * 1024) {
      setError(
        "La imagen no puede superar los 5 MB."
      );

      event.target.value = "";
      return;
    }

    clearMessages();

    setForm((previous) => ({
      ...previous,
      logo: file,
    }));

    const previewUrl =
      URL.createObjectURL(file);

    setImagePreview(previewUrl);
  };

  /* =======================================================
     REMOVE IMAGE
  ======================================================= */

  const removeImage = () => {
    setForm((previous) => ({
      ...previous,
      logo: null,
    }));

    setImagePreview(null);

    const input =
      document.getElementById(
        "store-logo-input"
      );

    if (input) {
      input.value = "";
    }
  };

  /* =======================================================
     RESET FORM
  ======================================================= */

  const resetForm = () => {
    setForm({
      ...INITIAL_FORM,
    });

    setEditingId(null);
    setImagePreview(null);

    const input =
      document.getElementById(
        "store-logo-input"
      );

    if (input) {
      input.value = "";
    }
  };

  /* =======================================================
     OPEN CREATE
  ======================================================= */

  const openCreateModal = () => {
    clearMessages();

    resetForm();

    setModalOpen(true);
  };

  /* =======================================================
     CLOSE MODAL
  ======================================================= */

  const closeModal = () => {
    if (saving) return;

    setModalOpen(false);

    resetForm();
  };

  /* =======================================================
     SAVE STORE
  ======================================================= */

  const saveStore = async () => {
    clearMessages();

    const cleanName =
      form.name.trim();

    const cleanCity =
      form.city.trim();

    if (!cleanName) {
      setError(
        "El nombre de la sede es obligatorio."
      );

      return;
    }

    if (!cleanCity) {
      setError(
        "La ciudad es obligatoria."
      );

      return;
    }

    try {
      setSaving(true);

      /*
       * Usamos FormData porque ahora
       * podemos enviar la imagen.
       */

      const formData = new FormData();

      formData.append(
        "name",
        cleanName
      );

      formData.append(
        "city",
        cleanCity
      );

      formData.append(
        "country",
        form.country.trim()
      );

      formData.append(
        "description",
        form.description.trim()
      );

      formData.append(
        "email",
        form.email.trim()
      );

      formData.append(
        "website",
        form.website.trim()
      );

      /*
       * Solo agregamos logo si
       * realmente seleccionó una imagen.
       */
      if (form.logo instanceof File) {
        formData.append(
          "logo",
          form.logo
        );
      }

      let response;

      if (editingId) {
        response = await API.patch(
          `/stores/${editingId}/`,
          formData,
          {
            headers: {
              "Content-Type":
                "multipart/form-data",
            },
          }
        );
      } else {
        response = await API.post(
          "/stores/",
          formData,
          {
            headers: {
              "Content-Type":
                "multipart/form-data",
            },
          }
        );
      }

      const saved =
        response?.data?.data ||
        response?.data;

      if (editingId) {
        setStores((previous) =>
          previous.map((store) =>
            store.id === editingId
              ? saved
              : store
          )
        );

        setSuccess(
          "Sede actualizada correctamente."
        );
      } else {
        setStores((previous) => [
          saved,
          ...previous,
        ]);

        setSuccess(
          "Nueva sede creada correctamente."
        );
      }

      setModalOpen(false);

      resetForm();
    } catch (err) {
      console.error(err);

      setError(
        getApiError(err)
      );
    } finally {
      setSaving(false);
    }
  };

  /* =======================================================
     EDIT STORE
  ======================================================= */

  const editStore = (store) => {
    clearMessages();

    setEditingId(store.id);

    setForm({
      name: store.name || "",
      city: store.city || "",
      country: store.country || "",
      description:
        store.description || "",
      email: store.email || "",
      website:
        store.website || "",
      logo: null,
    });

    /*
     * Si la sede ya tiene logo,
     * lo mostramos como preview.
     */
    if (store.logo) {
      setImagePreview(
        getImage(store.logo)
      );
    } else {
      setImagePreview(null);
    }

    setModalOpen(true);
  };

  /* =======================================================
     DELETE STORE
  ======================================================= */

  const deleteStore = async (id) => {
    const store =
      stores.find(
        (item) => item.id === id
      );

    const confirmed =
      window.confirm(
        `¿Eliminar la sede "${
          store?.name || "seleccionada"
        }"?`
      );

    if (!confirmed) return;

    try {
      clearMessages();

      setDeletingId(id);

      await API.delete(
        `/stores/${id}/`
      );

      setStores((previous) =>
        previous.filter(
          (item) => item.id !== id
        )
      );

      setSuccess(
        "Sede eliminada correctamente."
      );
    } catch (err) {
      console.error(err);

      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          "No fue posible eliminar la sede."
      );
    } finally {
      setDeletingId(null);
    }
  };

  /* =======================================================
     FILTER
  ======================================================= */

  const filteredStores = useMemo(() => {
    const query =
      search.trim().toLowerCase();

    if (!query) {
      return stores;
    }

    return stores.filter((store) =>
      `
        ${store.name || ""}
        ${store.city || ""}
        ${store.country || ""}
        ${store.description || ""}
        ${store.email || ""}
        ${store.website || ""}
      `
        .toLowerCase()
        .includes(query)
    );
  }, [stores, search]);

  /* =======================================================
     STATISTICS
  ======================================================= */

  const totalProducts = useMemo(() => {
    return stores.reduce(
      (total, store) =>
        total +
        getStoreProducts(store),
      0
    );
  }, [stores]);

  const totalCities = useMemo(() => {
    return new Set(
      stores
        .map((store) =>
          store.city
            ?.trim()
            .toLowerCase()
        )
        .filter(Boolean)
    ).size;
  }, [stores]);

  const storesWithWebsite =
    useMemo(() => {
      return stores.filter(
        (store) =>
          Boolean(store.website)
      ).length;
    }, [stores]);

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <section className="store-manager">

      {/* =================================================
          DECORATIVE BACKGROUND
      ================================================= */}

      <div className="store-manager__ambient store-manager__ambient--one" />

      <div className="store-manager__ambient store-manager__ambient--two" />

      {/* =================================================
          HEADER
      ================================================= */}

      <header className="store-manager__header">

        <div className="store-manager__heading">

          <div className="store-manager__eyebrow">
            <Sparkles size={13} />
            DEBUTS CROP · ADMIN / NETWORK
          </div>

          <div className="store-manager__title-row">

            <div className="store-manager__title-icon">
              <StoreIcon size={25} />
            </div>

            <div>
              <h1>
                Red de{" "}
                <span>Sedes</span>
              </h1>

              <p>
                Gestiona tus tiendas, puntos
                de atención y presencia
                comercial desde un solo lugar.
              </p>
            </div>

          </div>

        </div>

        <div className="store-manager__header-actions">

          <button
            type="button"
            className="store-manager__refresh"
            onClick={loadStores}
            disabled={loading}
            title="Actualizar sedes"
          >
            <RefreshCcw
              size={17}
              className={
                loading
                  ? "store-manager__spin"
                  : ""
              }
            />

            <span>
              Actualizar
            </span>
          </button>

          <button
            type="button"
            className="store-manager__primary"
            onClick={openCreateModal}
          >
            <Plus size={18} />
            Nueva sede
          </button>

        </div>

      </header>

      {/* =================================================
          STATUS
      ================================================= */}

      {error && (
        <div className="store-manager__message store-manager__message--error">

          <div className="store-manager__message-icon">
            <AlertCircle size={18} />
          </div>

          <span>{error}</span>

          <button
            type="button"
            onClick={() =>
              setError("")
            }
            aria-label="Cerrar mensaje"
          >
            <X size={15} />
          </button>

        </div>
      )}

      {success && (
        <div className="store-manager__message store-manager__message--success">

          <div className="store-manager__message-icon">
            <CheckCircle2 size={18} />
          </div>

          <span>{success}</span>

          <button
            type="button"
            onClick={() =>
              setSuccess("")
            }
            aria-label="Cerrar mensaje"
          >
            <X size={15} />
          </button>

        </div>
      )}

      {/* =================================================
          STATISTICS
      ================================================= */}

      <div className="store-manager__stats">

        <div className="store-stat">

          <div className="store-stat__icon">
            <Building2 size={19} />
          </div>

          <div className="store-stat__content">
            <span>Sedes</span>
            <strong>
              {stores.length}
            </strong>
            <small>
              puntos registrados
            </small>
          </div>

        </div>

        <div className="store-stat">

          <div className="store-stat__icon store-stat__icon--lime">
            <Map size={19} />
          </div>

          <div className="store-stat__content">
            <span>Ciudades</span>
            <strong>
              {totalCities}
            </strong>
            <small>
              ubicaciones
            </small>
          </div>

        </div>

        <div className="store-stat">

          <div className="store-stat__icon store-stat__icon--orange">
            <Package size={19} />
          </div>

          <div className="store-stat__content">
            <span>Productos</span>
            <strong>
              {totalProducts}
            </strong>
            <small>
              en la red
            </small>
          </div>

        </div>

        <div className="store-stat">

          <div className="store-stat__icon store-stat__icon--cyan">
            <Globe size={19} />
          </div>

          <div className="store-stat__content">
            <span>Web activa</span>
            <strong>
              {storesWithWebsite}
            </strong>
            <small>
              sedes con sitio
            </small>
          </div>

        </div>

        <div className="store-stat store-stat--brand">

          <div className="store-stat__brand">
            DC
          </div>

          <div className="store-stat__content">
            <span>DEBUTS CROP</span>
            <strong>
              NETWORK
            </strong>
            <small>
              store management
            </small>
          </div>

        </div>

      </div>

      {/* =================================================
          SEARCH
      ================================================= */}

      <div className="store-manager__toolbar">

        <div className="store-manager__toolbar-left">

          <div className="store-manager__search">

            <Search size={18} />

            <input
              type="text"
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
              placeholder="Buscar sede, ciudad, país, correo..."
              aria-label="Buscar sedes"
            />

            {search && (
              <button
                type="button"
                className="store-manager__search-clear"
                onClick={() =>
                  setSearch("")
                }
                aria-label="Limpiar búsqueda"
              >
                <X size={15} />
              </button>
            )}

          </div>

        </div>

        <div className="store-manager__toolbar-right">

          <div className="store-manager__network-status">
            <span />
            NETWORK ONLINE
          </div>

          <div className="store-manager__results">
            <strong>
              {filteredStores.length}
            </strong>

            <span>
              {filteredStores.length === 1
                ? " sede encontrada"
                : " sedes encontradas"}
            </span>
          </div>

        </div>

      </div>

      {/* =================================================
          CONTENT
      ================================================= */}

      {loading ? (
        <div className="store-manager__loading">

          <div className="store-manager__loader-ring">
            <Loader2
              size={32}
              className="store-manager__spin"
            />
          </div>

          <strong>
            Cargando red de sedes...
          </strong>

          <span>
            Sincronizando información
            de Debuts Crop.
          </span>

        </div>
      ) : filteredStores.length === 0 ? (

        <div className="store-manager__empty">

          <div className="store-manager__empty-grid" />

          <div className="store-manager__empty-icon">
            <StoreIcon size={34} />
          </div>

          <div className="store-manager__empty-code">
            NETWORK / 404
          </div>

          <h3>
            {search
              ? "No encontramos sedes"
              : "La red todavía está vacía"}
          </h3>

          <p>
            {search
              ? "Prueba con otro nombre, ciudad, país o correo."
              : "Crea la primera sede para comenzar a administrar tu red Debuts Crop."}
          </p>

          {search ? (
            <button
              type="button"
              className="store-manager__primary"
              onClick={() =>
                setSearch("")
              }
            >
              <Search size={17} />
              Ver todas
            </button>
          ) : (
            <button
              type="button"
              className="store-manager__primary"
              onClick={openCreateModal}
            >
              <Plus size={17} />
              Crear primera sede
            </button>
          )}

        </div>

      ) : (

        <div className="store-manager__grid">

          {filteredStores.map((store) => {

            const image =
              getImage(store.logo);

            const productCount =
              getStoreProducts(store);

            return (

              <article
                key={store.id}
                className="store-card"
              >

                {/* CARD MEDIA */}

                <div className="store-card__media">

                  {image ? (

                    <img
                      src={image}
                      alt={store.name}
                      className="store-card__image"
                      onError={(event) => {
                        event.currentTarget.style.display =
                          "none";
                      }}
                    />

                  ) : (

                    <div className="store-card__placeholder">

                      <div className="store-card__placeholder-letter">
                        {getStoreInitial(
                          store.name
                        )}
                      </div>

                      <StoreIcon size={20} />

                    </div>

                  )}

                  <div className="store-card__media-gradient" />

                  <div className="store-card__scanlines" />

                  <div className="store-card__media-top">

                    <span className="store-card__network-tag">
                      <span />
                      NETWORK
                    </span>

                    <span className="store-card__id">
                      #
                      {String(
                        store.id
                      ).padStart(3, "0")}
                    </span>

                  </div>

                  <div className="store-card__badge">
                    <CheckCircle2 size={13} />
                    Sede activa
                  </div>

                  <div className="store-card__media-mark">
                    DC
                  </div>

                </div>

                {/* CARD CONTENT */}

                <div className="store-card__content">

                  <div className="store-card__location">

                    <MapPin size={14} />

                    <span>
                      {store.city ||
                        "Sin ciudad"}

                      {store.country && (
                        <>
                          {" · "}
                          {store.country}
                        </>
                      )}
                    </span>

                  </div>

                  <div className="store-card__title-row">

                    <div>

                      <span className="store-card__overline">
                        DEBUTS CROP / STORE
                      </span>

                      <h2>
                        {store.name}
                      </h2>

                    </div>

                    <div className="store-card__arrow">
                      <ArrowUpRight
                        size={18}
                      />
                    </div>

                  </div>

                  <p className="store-card__description">
                    {store.description ||
                      "Esta sede todavía no tiene una descripción registrada."}
                  </p>

                  {/* METRICS */}

                  <div className="store-card__metrics">

                    <div className="store-card__metric">

                      <div className="store-card__metric-icon">
                        <Package size={15} />
                      </div>

                      <div>
                        <span>
                          Productos
                        </span>

                        <strong>
                          {productCount}
                        </strong>
                      </div>

                    </div>

                    <div className="store-card__metric">

                      <div className="store-card__metric-icon store-card__metric-icon--lime">
                        <Clock3 size={15} />
                      </div>

                      <div>
                        <span>
                          Estado
                        </span>

                        <strong className="is-active">
                          Activa
                        </strong>
                      </div>

                    </div>

                  </div>

                  {/* CONTACT */}

                  {(store.email ||
                    store.website) && (

                    <div className="store-card__contact">

                      {store.email && (

                        <a
                          href={`mailto:${store.email}`}
                          className="store-card__contact-item"
                        >
                          <Mail size={14} />

                          <span>
                            {store.email}
                          </span>
                        </a>

                      )}

                      {store.website && (

                        <a
                          href={
                            store.website.startsWith(
                              "http"
                            )
                              ? store.website
                              : `https://${store.website}`
                          }
                          target="_blank"
                          rel="noreferrer"
                          className="store-card__contact-item"
                        >

                          <Globe size={14} />

                          <span>
                            {store.website}
                          </span>

                          <ExternalLink
                            size={12}
                          />

                        </a>

                      )}

                    </div>

                  )}

                  {/* PRODUCTS */}

                  {store.products?.length > 0 && (

                    <div className="store-card__products">

                      <div className="store-card__products-heading">

                        <span>
                          Productos destacados
                        </span>

                        <Package size={14} />

                      </div>

                      <div className="store-card__product-list">

                        {store.products
                          .slice(0, 3)
                          .map((product) => (

                            <div
                              key={product.id}
                              className="store-product"
                            >

                              <span>
                                {product.name}
                              </span>

                              <strong>
                                {product.formatted_price ||
                                  product.price ||
                                  "—"}
                              </strong>

                            </div>

                          ))}

                      </div>

                      {store.products.length >
                        3 && (

                        <span className="store-card__more">

                          +
                          {store.products.length -
                            3}{" "}
                          productos más

                        </span>

                      )}

                    </div>

                  )}

                </div>

                {/* ACTIONS */}

                <div className="store-card__actions">

                  <button
                    type="button"
                    className="store-card__edit"
                    onClick={() =>
                      editStore(store)
                    }
                  >
                    <Pencil size={15} />
                    Editar sede
                  </button>

                  <button
                    type="button"
                    className="store-card__delete"
                    onClick={() =>
                      deleteStore(
                        store.id
                      )
                    }
                    disabled={
                      deletingId ===
                      store.id
                    }
                    title="Eliminar sede"
                  >

                    {deletingId ===
                    store.id ? (
                      <Loader2
                        size={16}
                        className="store-manager__spin"
                      />
                    ) : (
                      <Trash2 size={16} />
                    )}

                  </button>

                </div>

              </article>

            );
          })}

        </div>

      )}

      {/* =================================================
          MODAL
      ================================================= */}

      {modalOpen && (

        <div
          className="store-modal__overlay"
          onMouseDown={(event) => {

            if (
              event.target ===
                event.currentTarget &&
              !saving
            ) {
              closeModal();
            }

          }}
        >

          <div
            className="store-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="store-modal-title"
          >

            {/* DECORATION */}

            <div className="store-modal__glow store-modal__glow--pink" />

            <div className="store-modal__glow store-modal__glow--lime" />

            {/* HEADER */}

            <div className="store-modal__header">

              <div className="store-modal__header-left">

                <div className="store-modal__icon">

                  {editingId ? (
                    <Pencil size={20} />
                  ) : (
                    <StoreIcon size={20} />
                  )}

                </div>

                <div>

                  <div className="store-modal__eyebrow">
                    <Sparkles size={12} />
                    DEBUTS CROP · STORE MANAGER
                  </div>

                  <div className="store-modal__title-row">

                    <h2 id="store-modal-title">
                      {editingId
                        ? "Editar sede"
                        : "Nueva sede"}
                    </h2>

                    <span className="store-modal__status">
                      <span />

                      {editingId
                        ? `ID ${editingId}`
                        : "NEW STORE"}
                    </span>

                  </div>

                  <p>
                    {editingId
                      ? "Actualiza la información, imagen y presencia de esta sede."
                      : "Registra un nuevo punto de atención para la red Debuts Crop."}
                  </p>

                </div>

              </div>

              <button
                type="button"
                className="store-modal__close"
                onClick={closeModal}
                disabled={saving}
                aria-label="Cerrar ventana"
              >
                <X size={19} />
              </button>

            </div>

            {/* BODY */}

            <div className="store-modal__body">

              {/* =========================================
                  VISUAL / IMAGE PANEL
              ========================================= */}

              <aside className="store-modal__visual">

                <div className="store-modal__visual-grid" />

                <div className="store-modal__visual-scanlines" />

                <div className="store-modal__visual-label">
                  STORE / NETWORK
                </div>

                {/* IMAGE UPLOAD */}

                <label
                  className={`store-modal__image-upload ${
                    imagePreview
                      ? "has-image"
                      : ""
                  }`}
                  htmlFor="store-logo-input"
                >

                  {imagePreview ? (

                    <img
                      src={imagePreview}
                      alt="Vista previa de la sede"
                      className="store-modal__preview-image"
                    />

                  ) : (

                    <div className="store-modal__upload-placeholder">

                      <div className="store-modal__upload-icon">
                        <Camera size={28} />
                      </div>

                      <strong>
                        Subir imagen
                      </strong>

                      <span>
                        Logo o foto de la sede
                      </span>

                    </div>

                  )}

                  <div className="store-modal__image-overlay">

                    <Upload size={17} />

                    <span>
                      {imagePreview
                        ? "Cambiar imagen"
                        : "Seleccionar imagen"}
                    </span>

                  </div>

                </label>

                <input
                  id="store-logo-input"
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/jpg"
                  onChange={
                    handleImageChange
                  }
                  hidden
                />

                {imagePreview && (

                  <button
                    type="button"
                    className="store-modal__remove-image"
                    onClick={
                      removeImage
                    }
                  >
                    <Trash2 size={13} />
                    Quitar imagen
                  </button>

                )}

                {/* STORE DATA */}

                <div className="store-modal__visual-brand">

                  <strong>
                    {form.name ||
                      "DEBUTS CROP"}
                  </strong>

                  <span>
                    {form.city ||
                      "NEW LOCATION"}

                    {form.country
                      ? ` · ${form.country}`
                      : ""}
                  </span>

                </div>

                <div className="store-modal__visual-meta">

                  <div>
                    <MapPin size={14} />

                    <span>
                      {form.city ||
                        "Ciudad pendiente"}
                    </span>
                  </div>

                  <div>
                    <Network size={14} />

                    <span>
                      NETWORK ACTIVE
                    </span>
                  </div>

                </div>

                <div className="store-modal__visual-corner">
                  01 / DC
                </div>

              </aside>

              {/* =========================================
                  FORM
              ========================================= */}

              <div className="store-modal__form">

                {/* MAIN INFO */}

                <div className="store-form__section">

                  <div className="store-form__section-heading">

                    <div className="store-form__section-title">

                      <Building2 size={16} />

                      <span>
                        Información principal
                      </span>

                    </div>

                    <span>
                      01
                    </span>

                  </div>

                  <div className="store-form__grid">

                    <label className="store-field store-field--full">

                      <span>
                        Nombre de la sede
                        <b>*</b>
                      </span>

                      <div className="store-field__input-wrap">

                        <StoreIcon size={15} />

                        <input
                          name="name"
                          value={
                            form.name
                          }
                          onChange={
                            handleChange
                          }
                          placeholder="Ej. Debuts Crop Medellín"
                          autoFocus
                        />

                      </div>

                    </label>

                    <label className="store-field">

                      <span>
                        Ciudad
                        <b>*</b>
                      </span>

                      <div className="store-field__input-wrap">

                        <MapPin size={15} />

                        <input
                          name="city"
                          value={
                            form.city
                          }
                          onChange={
                            handleChange
                          }
                          placeholder="Ej. Medellín"
                        />

                      </div>

                    </label>

                    <label className="store-field">

                      <span>
                        País
                      </span>

                      <div className="store-field__input-wrap">

                        <Globe size={15} />

                        <input
                          name="country"
                          value={
                            form.country
                          }
                          onChange={
                            handleChange
                          }
                          placeholder="Ej. Colombia"
                        />

                      </div>

                    </label>

                  </div>

                </div>

                {/* CONTACT */}

                <div className="store-form__section">

                  <div className="store-form__section-heading">

                    <div className="store-form__section-title">

                      <Mail size={16} />

                      <span>
                        Contacto digital
                      </span>

                    </div>

                    <span>
                      02
                    </span>

                  </div>

                  <div className="store-form__grid">

                    <label className="store-field">

                      <span>
                        Correo electrónico
                      </span>

                      <div className="store-field__input-wrap">

                        <Mail size={15} />

                        <input
                          type="email"
                          name="email"
                          value={
                            form.email
                          }
                          onChange={
                            handleChange
                          }
                          placeholder="sede@debutscrop.com"
                        />

                      </div>

                    </label>

                    <label className="store-field">

                      <span>
                        Sitio web
                      </span>

                      <div className="store-field__input-wrap">

                        <Globe size={15} />

                        <input
                          type="text"
                          name="website"
                          value={
                            form.website
                          }
                          onChange={
                            handleChange
                          }
                          placeholder="https://debutscrop.com"
                        />

                      </div>

                    </label>

                    <label className="store-field store-field--full">

                      <span>
                        Descripción
                      </span>

                      <textarea
                        name="description"
                        value={
                          form.description
                        }
                        onChange={
                          handleChange
                        }
                        placeholder="Describe esta sede, ubicación, servicios o características..."
                        rows={5}
                      />

                      <small>
                        Esta información
                        aparecerá en la
                        tarjeta de la sede.
                      </small>

                    </label>

                  </div>

                </div>

                {/* IMAGE INFO */}

                <div className="store-form__image-info">

                  <div className="store-form__image-info-icon">
                    <ImageIcon size={18} />
                  </div>

                  <div>

                    <strong>
                      Imagen de sede
                    </strong>

                    <span>
                      PNG, JPG o WEBP · máximo
                      5 MB
                    </span>

                  </div>

                  {imagePreview && (
                    <CheckCircle2
                      size={17}
                      className="store-form__image-check"
                    />
                  )}

                </div>

                {/* LIVE PREVIEW */}

                <div className="store-form__preview">

                  <div className="store-form__preview-icon">
                    <LocateFixed size={17} />
                  </div>

                  <div>

                    <span>
                      PREVISUALIZACIÓN
                    </span>

                    <strong>
                      {form.name ||
                        "Nombre de la sede"}
                    </strong>

                    <small>
                      {form.city ||
                        "Ciudad"}

                      {form.country
                        ? ` · ${form.country}`
                        : ""}
                    </small>

                  </div>

                  <ArrowUpRight
                    size={18}
                  />

                </div>

              </div>

            </div>

            {/* FOOTER */}

            <div className="store-modal__footer">

              <div className="store-modal__footer-info">

                <span>
                  <span />
                  Campos obligatorios
                </span>

                <small>
                  ESC para cerrar
                </small>

              </div>

              <div className="store-modal__footer-actions">

                <button
                  type="button"
                  className="store-modal__cancel"
                  onClick={closeModal}
                  disabled={saving}
                >
                  Cancelar
                </button>

                <button
                  type="button"
                  className="store-modal__save"
                  onClick={saveStore}
                  disabled={saving}
                >

                  {saving ? (
                    <>
                      <Loader2
                        size={17}
                        className="store-manager__spin"
                      />

                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save size={17} />

                      {editingId
                        ? "Guardar cambios"
                        : "Crear sede"}
                    </>
                  )}

                </button>

              </div>

            </div>

          </div>

        </div>

      )}

    </section>
  );
}